<script setup lang="ts">
import { useMutationObserver, useDebounceFn, useEventListener, useResizeObserver } from '@vueuse/core';

import { usePDFJs } from '../../composables/use-pdf-js';
import { PDF_SCALES } from '../../constants/pdf';

export type PDFVisualizerEmits = {
  (e: 'page-init'): void;
  (e: 'pages-load', pagesCount: number): void;
  (e: 'page-change', pageNumber: number): void;
  (e: 'document-load'): void;
  (e: 'scale-changing'): void;
};

export type PDFVisualizerProps = {
  documentPdfUrl: string;
  initialScale?: string;
  containerClass?: string;
  relative?: boolean;
  limitPdfToScale1?: boolean;
};

type Rect = {
  width: number;
  height: number;
};

const emit = defineEmits<PDFVisualizerEmits>();
const props = withDefaults(defineProps<PDFVisualizerProps>(), {
  initialScale: PDF_SCALES.AUTO,
  containerClass: '',
});

let oldOffsetRect: Rect | undefined;
const pdfViewerEl = ref();
const viewerContainerEl = ref();
const containerHeight = ref<string>();
const containerStyle = computed(() => ({ height: containerHeight.value }));

function adjustHeight() {
  const { top } = viewerContainerEl.value.getBoundingClientRect();
  const border = 1;
  const remainingHeight = window.innerHeight - top - border;

  containerHeight.value = `${remainingHeight}px`;
}

const limitPdfToScale1 = toRef(props, 'limitPdfToScale1');

const handleMutation = useDebounceFn(() => requestAnimationFrame(adjustHeight), 50);
const { increaseZoom, decreaseZoom, resetZoom } = usePDFJs(() => props.documentPdfUrl, viewerContainerEl, {
  initialScale: props.initialScale,
  onPageInit: () => emit('page-init'),
  onDocumentLoad: () => emit('document-load'),
  onPageChange: ({ pageNumber }) => emit('page-change', pageNumber),
  onPagesLoad: (pagesCount) => emit('pages-load', pagesCount),
  onScaleChange: () => emit('scale-changing'),
  limitPdfToScale1,
});

useMutationObserver(pdfViewerEl, handleMutation, { childList: true });
useEventListener(window, 'resize', handleMutation, { passive: true });

useResizeObserver(viewerContainerEl, (entries) => {
  const [entry] = entries;

  if (!(entry?.target instanceof HTMLElement)) return;

  const oldRect = oldOffsetRect;
  const currentRect = {
    width: entry.target.offsetWidth,
    height: entry.target.offsetHeight,
  };

  oldOffsetRect = currentRect;

  if (!oldRect) return;
  if (currentRect.width !== oldRect.width) return resetZoom();
  if (currentRect.height !== oldRect.height) return resetZoom();
});

defineExpose({
  resetZoom,
  decreaseZoom,
  increaseZoom,
});
</script>

<template>
  <div
    :class="[$style.wrapper, {[$style.wrapperRelative]: relative }]"
    :style="containerStyle"
    data-testid="documentPreview"
  >
    <div
      ref="viewerContainerEl"
      data-testid="pdfVisualizerViewerContainer"
      :style="containerStyle"
      :class="[$style.viewerContainer, containerClass]"
    >
      <div
        id="viewer"
        ref="pdfViewerEl"
        class="pdfViewer js-pdf-viewer"
        :class="$style.pdfViewer"
      />
    </div>
  </div>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  justify-content: center;
  height: 100%;

  :global {
    .page[data-page-number] {
      --page-border: none;
      --page-border-image: none;
      display: block;
      margin: auto;
      margin-bottom: var(--space-small-x);
      box-shadow: var(--shadow-medium);
    }

    .textLayer [role='presentation'],
    .textLayer span {
      // Remove global line-height of 1.5 applied by typography.scss
      line-height: var(--line-height-small);
    }
  }
}

.wrapperRelative {
  position: relative;
}

.viewerContainer {
  position: absolute;
  overflow-y: auto;
  width: 100%;
  height: 100%;
  padding-bottom: var(--space-large);
  scroll-padding: var(--scroll-padding);
}

.pdfViewer {
  max-width: 100%;
}
</style>
